/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: AuditLogsPage.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Main page for Audit Logs with list view and filters
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { FileText, RefreshCw, FilterX } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useAuditLogList } from './hooks/useAuditLogList';
import { AuditLog } from './types/auditLog.types';
import { AuditLogGrid } from './components/AuditLogGrid';
import { MetadataModal } from './components/MetadataModal';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FixedActionBar from '../../components/ui/FixedActionBar';

export default function AuditLogsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const pageTitle = useMemo(() => t('auditLogs.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
  } = useAuditLogList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: AuditLog) => (
              <span className="text-sm text-foreground">
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleString()
                  : '—'}
              </span>
            )
          };
        }
        if (column.key === 'action') {
          return {
            ...column,
            render: (_value: unknown, item: AuditLog) => {
              const actionColors: Record<string, string> = {
                CREATE: 'text-green-600 dark:text-green-400',
                UPDATE: 'text-blue-600 dark:text-blue-400',
                DELETE: 'text-red-600 dark:text-red-400',
                LOGIN: 'text-purple-600 dark:text-purple-400',
                LOGOUT: 'text-gray-600 dark:text-gray-400',
              };
              const colorClass = actionColors[item.action] || 'text-foreground';
              return (
                <span className={`text-sm font-medium ${colorClass}`}>
                  {item.action}
                </span>
              );
            }
          };
        }
        if (column.key === 'resource') {
          return {
            ...column,
            render: (_value: unknown, item: AuditLog) => (
              <span className="text-sm text-muted-foreground">
                {item.resource || '—'}
              </span>
            )
          };
        }
        if (column.key === 'user') {
          return {
            ...column,
            render: (_value: unknown, item: AuditLog) => (
              <div className="text-sm">
                <div className="text-foreground font-medium">
                  {item.user?.fullName || item.user?.username || '—'}
                </div>
                {item.user?.username && item.user?.fullName && (
                  <div className="text-xs text-muted-foreground">
                    @{item.user.username}
                  </div>
                )}
              </div>
            )
          };
        }
        if (column.key === 'ipAddress') {
          return {
            ...column,
            render: (_value: unknown, item: AuditLog) => (
              <span className="text-xs font-mono text-muted-foreground">
                {item.ipAddress || '—'}
              </span>
            )
          };
        }
        if (column.key === 'metadata') {
          return {
            ...column,
            render: (_value: unknown, item: AuditLog) => {
              const hasMetadata = item.metadata && Object.keys(item.metadata).length > 0;
              
              if (!hasMetadata) {
                return <span className="text-muted-foreground">—</span>;
              }
              
              return (
                <SimpleTooltip content="View metadata">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedLog(item)}
                    className="h-8 px-2"
                  >
                    <FileText className="h-4 w-4 text-primary" />
                  </Button>
                </SimpleTooltip>
              );
            }
          };
        }
        return column;
      }),
    [baseColumns]
  );

  // Check if any filter has been set
  const hasActiveFilters = !!(
    filters.startDate ||
    filters.endDate ||
    filters.actions.length > 0 ||
    filters.resource ||
    filters.userId
  );

  return (
    <>
      <ModulePageContainer
        currentView="list"
        listView={
          <AuditLogGrid
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onFilterChange={handleFilterChange}
          />
        }
        formView={null}
      />

      {/* Fixed Action Bar */}
      <FixedActionBar
        onReset={hasActiveFilters ? handleClearFilters : undefined}
        onSave={handleApplyFilters}
        resetText={
          <>
            <FilterX className="h-4 w-4 mr-2" />
            {t('auditLogs.filters.clear')}
          </>
        }
        saveText={
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('auditLogs.filters.refresh')}
          </>
        }
        showCancel={false}
        showDelete={false}
        isSaving={loading}
      />

      {/* Metadata Modal */}
      {selectedLog && (
        <MetadataModal
          open={!!selectedLog}
          metadata={selectedLog.metadata}
          action={selectedLog.action}
          resource={selectedLog.resource}
          timestamp={selectedLog.createdAt}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </>
  );
}

